import {
  useEffect,
  useLayoutEffect,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type MutableRefObject,
} from 'react';
import {
  toLines,
  lineLengths,
  offsetToLineCol,
  lineColToOffset,
  listShapeChanged,
  boldShapeChanged,
  foldValue,
  segmentLine,
  toggleBold,
} from '../ce-model';

/**
 * THE MULTILINE COMPOSER SURFACE — one contenteditable, no mirror.
 *
 * The update box was a transparent <textarea> over a coloured mirror div, two
 * layers that had to wrap at the very same width or the caret drifted off the
 * letters (a bug fixed twice, once per platform). Here the caret lives in the one
 * element that draws the text, so there is nothing to keep in step — and because
 * each line is its own block, a wrapped list item can hang its second line under
 * the first letter of its text, the way a word processor does, WHILE you type.
 *
 * React must never own this element's children, or it would fight the browser's
 * own editing on every keystroke and throw the caret away. So the element is
 * rendered EMPTY and its contents are built here by hand (buildDom), and rebuilt
 * only when the list SHAPE changes — a line gained or lost a marker, or there is a
 * new line — never on ordinary typing, where the browser's edit already stands and
 * the caret must be left exactly where it is. The caret is saved and restored as a
 * plain-text offset through the pure map in ce-model (unit-tested on Node).
 */

export type Run = { text: string; mention?: string };

export interface MentionEditableHandle {
  /** Focus the field and put the caret at the end of its text. */
  focus: () => void;
  /** The current selection as plain-text offsets (start === end when collapsed),
   *  so the parent can edit the value around it (a Shift+Enter newline) without
   *  reaching into the DOM. */
  selection: () => { start: number; end: number };
  /** The LIVE text the field currently shows, read straight from the DOM. The
   *  parent's React `value` prop can trail the DOM by a tick right after a
   *  keystroke (the input event's state flush has not landed before the next
   *  key's handler runs — WebKit is laxer about this than Blink). A newline
   *  decision reads the caret from this same DOM, so it must read the text from
   *  here too, or value and caret disagree and a "1. " line fails to continue. */
  value: () => string;
  /** The editable element itself — for a caller that must scroll it into view. */
  node: () => HTMLElement | null;
}

export function MentionEditable({
  value,
  tint,
  placeholder,
  className,
  autoFocus,
  ariaLabel,
  disabled,
  listId,
  open,
  matchesLen,
  activeIdx,
  caretRef,
  onInput,
  onCaret,
  onKeyDown,
  handleRef,
}: {
  value: string;
  /** A line's text → its plain and @mention runs, so mentions tint inline. */
  tint: (line: string) => Run[];
  placeholder: string;
  className: string;
  autoFocus?: boolean;
  ariaLabel?: string;
  /** Read-only: shows the text but cannot be edited (a field the viewer may not
   *  change). No caret, no autofocus. */
  disabled?: boolean;
  listId: string;
  open: boolean;
  matchesLen: number;
  activeIdx: number;
  /** The caret to place after the NEXT rebuild (a completed mention, an
   *  auto-formatted list newline). Read and cleared here once applied. Shared with
   *  the single-line field's own restore, so only the live surface consumes it. */
  caretRef: MutableRefObject<number | null>;
  onInput: (value: string, caret: number) => void;
  /** A caret move (no text change). The LIVE value is passed with it: right after
   *  a keystroke the parent's React value prop is a tick stale, and deciding
   *  whether to open the @picker on stale text would flicker it shut. */
  onCaret: (value: string, caret: number) => void;
  onKeyDown: (e: ReactKeyboardEvent<HTMLDivElement>) => void;
  handleRef?: MutableRefObject<MentionEditableHandle | null>;
}) {
  const elRef = useRef<HTMLDivElement>(null);
  // The value the DOM currently shows. Ordinary typing keeps this in step without
  // a rebuild; a value that differs here is a structural change that needs one.
  const domValue = useRef<string | null>(null);
  // A selection (not just a caret) to restore after the NEXT rebuild — ⌘B keeps the
  // run it just bolded highlighted, so a second ⌘B toggles it straight back off,
  // the way a word processor does. Takes priority over caretRef when both are set.
  const selRef = useRef<{ start: number; end: number } | null>(null);
  // A hidden ruler, same font as the field, for the EXACT pixel width of a line's
  // marker — so the hang lands under the content's first letter, not a ch-guess.
  const rulerRef = useRef<HTMLSpanElement | null>(null);
  const widthCache = useRef(new Map<string, number>());

  // --- caret: plain-text offset <-> a real DOM selection -------------------
  // The plain-text offset of a (node, offset) DOM point inside this field.
  const offsetAt = (node: Node, nodeOffset: number): number => {
    const el = elRef.current;
    if (!el || !el.contains(node)) return value.length;
    const lines = [...el.children];
    let line = 0;
    let col = 0;
    for (let i = 0; i < lines.length; i++) {
      if (lines[i] === node || lines[i].contains(node)) {
        line = i;
        col = textBefore(lines[i], node, nodeOffset);
        break;
      }
    }
    return lineColToOffset(lineLengths(readValue()), line, col);
  };

  const readCaret = (): number => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return value.length;
    const r = sel.getRangeAt(0);
    return offsetAt(r.startContainer, r.startOffset);
  };

  const readSelection = (): { start: number; end: number } => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return { start: value.length, end: value.length };
    const r = sel.getRangeAt(0);
    const a = offsetAt(r.startContainer, r.startOffset);
    const b = offsetAt(r.endContainer, r.endOffset);
    return { start: Math.min(a, b), end: Math.max(a, b) };
  };

  // Characters before (node, offset) within a line block, walking its text nodes.
  const textBefore = (block: Element, node: Node, offset: number): number => {
    if (node === block) {
      // Offset is an index among the block's child nodes.
      let n = 0;
      for (let i = 0; i < offset && i < block.childNodes.length; i++) n += (block.childNodes[i].textContent ?? '').length;
      return n;
    }
    let n = 0;
    const walk = document.createTreeWalker(block, NodeFilter.SHOW_TEXT);
    let t: Node | null;
    while ((t = walk.nextNode())) {
      if (t === node) return n + Math.min(offset, (t.textContent ?? '').length);
      n += (t.textContent ?? '').length;
    }
    return n;
  };

  const readValue = (): string => {
    const el = elRef.current;
    if (!el) return value;
    // Each top-level child is normally a line block and the value is their text
    // joined by newlines. foldValue also recovers stray top-level text the browser
    // can leave at the root (Chrome/Edge on Windows) instead of inside a line, so a
    // just-typed character is never read back as "" — which left the Post button
    // dark for those users. An empty block (a lone <br>) still reads as "".
    return foldValue(
      [...el.childNodes].map((node) => ({
        isBlock: node.nodeType === 1 && node.nodeName === 'DIV',
        text: node.textContent ?? '',
      }))
    );
  };

  const placeCaret = (offset: number) => {
    const el = elRef.current;
    if (!el) return;
    const lens = lineLengths(readValue());
    const { line, col } = offsetToLineCol(lens, offset);
    const block = el.children[line];
    if (!block) return;
    const { node, pos } = nodeAtCol(block, col);
    const sel = window.getSelection();
    if (!sel) return;
    const range = document.createRange();
    range.setStart(node, pos);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
  };

  // Select a range of the text, given as two plain-text offsets — the same map as
  // placeCaret, at both ends. Used to keep a just-bolded run highlighted, and for
  // ⌘A (select all, which a bare contenteditable does not do on its own).
  const placeSelection = (start: number, end: number) => {
    const el = elRef.current;
    if (!el) return;
    const lens = lineLengths(readValue());
    const a = offsetToLineCol(lens, start);
    const b = offsetToLineCol(lens, end);
    const blockA = el.children[a.line];
    const blockB = el.children[b.line];
    if (!blockA || !blockB) return;
    const A = nodeAtCol(blockA, a.col);
    const B = nodeAtCol(blockB, b.col);
    const sel = window.getSelection();
    if (!sel) return;
    const range = document.createRange();
    range.setStart(A.node, A.pos);
    range.setEnd(B.node, B.pos);
    sel.removeAllRanges();
    sel.addRange(range);
  };

  // Keep the caret in view by scrolling the FIELD's own overflow — never an
  // ancestor, so the modal around it does not move. Needed only after a rebuild:
  // buildDom wipes el.textContent (which resets scrollTop to 0, pinning the field
  // to the top) and the caret is then placed programmatically, which — unlike a
  // real keystroke — does not make the browser reveal it. So a new list item added
  // below the fold would be left out of sight until the next ordinary keystroke.
  const scrollCaretIntoView = () => {
    const el = elRef.current;
    const sel = window.getSelection();
    if (!el || !sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (!el.contains(range.startContainer)) return;
    let rect = range.getBoundingClientRect();
    // A collapsed caret at a text-node boundary can measure as an empty rect in
    // WebKit; fall back to the caret's own line block.
    if (!rect || (rect.top === 0 && rect.bottom === 0 && rect.height === 0)) {
      const n = range.startContainer;
      const block = n.nodeType === 1 ? (n as Element) : n.parentElement;
      const br = block?.getBoundingClientRect();
      if (br) rect = br;
    }
    const view = el.getBoundingClientRect();
    const pad = 4;
    if (rect.bottom > view.bottom - pad) el.scrollTop += rect.bottom - (view.bottom - pad);
    else if (rect.top < view.top + pad) el.scrollTop -= view.top + pad - rect.top;
  };

  // The text node and offset at column `col` within a line block.
  const nodeAtCol = (block: Element, col: number): { node: Node; pos: number } => {
    let n = 0;
    const walk = document.createTreeWalker(block, NodeFilter.SHOW_TEXT);
    let t: Node | null;
    let last: Node | null = null;
    while ((t = walk.nextNode())) {
      const len = (t.textContent ?? '').length;
      if (col <= n + len) return { node: t, pos: col - n };
      n += len;
      last = t;
    }
    if (last) return { node: last, pos: (last.textContent ?? '').length };
    // An empty line (lone <br>): anchor on the block itself, before the <br>.
    return { node: block, pos: 0 };
  };

  // --- building the DOM from the value -------------------------------------
  const markerWidth = (marker: string): number => {
    if (marker === '') return 0;
    const cached = widthCache.current.get(marker);
    if (cached != null) return cached;
    const ruler = rulerRef.current;
    if (!ruler) return 0;
    ruler.textContent = marker;
    const w = ruler.getBoundingClientRect().width;
    widthCache.current.set(marker, w);
    return w;
  };

  // The ruler must measure in the FIELD's own font (statuslog is 13px, the editor
  // 14px), not whatever it inherits sitting beside it. Copy the field's metrics
  // onto it before a build; a build is rare (structural change only), so clearing
  // the width cache here costs nothing and covers a theme/size change.
  const syncRuler = () => {
    const el = elRef.current;
    const ruler = rulerRef.current;
    if (!el || !ruler) return;
    const cs = getComputedStyle(el);
    ruler.style.fontFamily = cs.fontFamily;
    ruler.style.fontSize = cs.fontSize;
    ruler.style.fontWeight = cs.fontWeight;
    ruler.style.fontStyle = cs.fontStyle;
    ruler.style.letterSpacing = cs.letterSpacing;
    widthCache.current.clear();
  };

  // Append a run of text to a parent, tinting its @mentions — the same split the
  // feed reads back, so an @name is coloured here exactly as it will be once saved.
  // Used for both plain text and the inside of a bold run.
  const appendRuns = (parent: Node, text: string) => {
    for (const run of tint(text)) {
      if (run.mention) {
        const span = document.createElement('span');
        span.className = 'mention';
        span.textContent = run.text;
        parent.appendChild(span);
      } else {
        parent.appendChild(document.createTextNode(run.text));
      }
    }
  };

  const buildDom = () => {
    const el = elRef.current;
    if (!el) return;
    syncRuler();
    el.textContent = '';
    for (const { text, hang } of toLines(value)) {
      const block = document.createElement('div');
      block.className = 'ce-line';
      if (hang > 0) {
        const w = markerWidth(text.slice(0, hang));
        // Indent the whole list item — marker and all — to the SAME depth the
        // posted feed uses (.md-list padding-left: 20px), so a list reads the same
        // while you type as once it is saved: the item's text sits one indent in
        // and the marker hangs in the gutter beside it, and a wrapped line tucks
        // back under that text. (Before this the marker sat flush at the margin, so
        // an unwrapped item looked unindented until it was posted.) The text column
        // is max(indent, marker) so a very wide marker never spills off the left.
        const LIST_INDENT = 20;
        block.style.paddingLeft = `${Math.max(LIST_INDENT, w)}px`;
        block.style.textIndent = `${-w}px`;
      }
      if (text === '') {
        if (value === '') block.dataset.placeholder = placeholder;
        block.appendChild(document.createElement('br'));
      } else {
        // Draw the line bold-aware. A bold run is its two `**` markers (kept as
        // real text, painted zero-width) around the inner text drawn heavy; plain
        // and bold-inner text are both still @mention-tinted. The pieces carry
        // every character of the line, so the block reads back as the value — the
        // caret arithmetic above is untouched (see ce-model segmentLine).
        for (const seg of segmentLine(text)) {
          if (seg.kind === 'mark') {
            const mark = document.createElement('span');
            mark.className = 'ce-bold-mark';
            mark.textContent = seg.text;
            block.appendChild(mark);
          } else if (seg.kind === 'bold') {
            const strong = document.createElement('strong');
            appendRuns(strong, seg.text);
            block.appendChild(strong);
          } else {
            appendRuns(block, seg.text);
          }
        }
      }
      el.appendChild(block);
    }
    domValue.current = value;
    el.classList.toggle('is-empty', value === '');
  };

  // Rebuild whenever the value prop differs from what the DOM shows (a structural
  // change or a programmatic edit), then honour a pending caret. Ordinary typing
  // sets domValue to the new text in the input handler, so value === domValue here
  // and the DOM — with the browser's own edit and caret — is left untouched.
  useLayoutEffect(() => {
    if (value !== domValue.current) buildDom();
    if (selRef.current != null) {
      // A ⌘B that bolded a run: keep that run selected rather than collapsing to a
      // caret, so a second ⌘B can toggle it back.
      const el = elRef.current;
      el?.focus();
      placeSelection(selRef.current.start, selRef.current.end);
      scrollCaretIntoView();
      selRef.current = null;
      caretRef.current = null;
    } else if (caretRef.current != null) {
      const el = elRef.current;
      el?.focus();
      placeCaret(caretRef.current);
      scrollCaretIntoView();
      caretRef.current = null;
    }
    // placeCaret/buildDom read live refs; value is the only trigger that matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Mount: build once, and open with the caret at the end so "@ to mention" works
  // straight away even when the editor opens with text already in it.
  useLayoutEffect(() => {
    buildDom();
    if (autoFocus && !disabled) {
      const el = elRef.current;
      el?.focus();
      placeCaret(value.length);
      onCaret(value, value.length);
    }
    if (handleRef)
      handleRef.current = {
        focus: () => {
          elRef.current?.focus();
          placeCaret(readValue().length);
        },
        selection: readSelection,
        value: readValue,
        node: () => elRef.current,
      };
    return () => {
      if (handleRef) handleRef.current = null;
    };
    // Mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track the caret whenever the selection moves inside this field without typing
  // (a click, an arrow key), so the picker reads the right place.
  useEffect(() => {
    const onSel = () => {
      const el = elRef.current;
      const sel = window.getSelection();
      if (!el || !sel || sel.rangeCount === 0) return;
      if (!el.contains(sel.getRangeAt(0).startContainer)) return;
      onCaret(readValue(), readCaret());
    };
    document.addEventListener('selectionchange', onSel);
    return () => document.removeEventListener('selectionchange', onSel);
    // onCaret/readCaret read live refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInput = () => {
    const el = elRef.current;
    const next = readValue();
    const caret = readCaret();
    const prev = domValue.current ?? '';
    // The browser can drift from the canonical one-`.ce-line`-block-per-line shape —
    // most visibly Chrome/Edge on Windows, which can drop a typed character into a
    // bare text node at the root (readValue folds it back in, so `next` still holds
    // it). Force a rebuild to restore the blocks, the same as a real structural
    // change, so the drift heals on the very next input rather than leaving the
    // field permanently out of step with its value. Otherwise rebuild when the list
    // SHAPE changes (a line gained or lost a marker, or a line was added) so a hang
    // is applied or dropped — or when a line's BOLD shape changes (a run turned bold
    // or back to plain, e.g. an empty `****` became `**x**`) so it is drawn heavy or
    // plain — and when the field goes empty or leaves empty, so the
    // placeholder shows and a stray <br> the browser leaves behind an emptied block
    // is cleared. Leave domValue stale so the effect rebuilds, and ask it to put the
    // caret back where it is now. Otherwise the browser's own edit already stands:
    // just record it and leave the caret alone.
    const drifted =
      !!el && [...el.childNodes].some((n) => !(n.nodeType === 1 && (n as Element).classList.contains('ce-line')));
    if (drifted || listShapeChanged(prev, next) || boldShapeChanged(prev, next) || (prev === '') !== (next === '')) {
      // A caret offset read from a stray node is unreliable (it is in no line
      // block); a drift is the browser appending, so land at the end of the healed
      // text. A clean structural change keeps the caret read from the live DOM.
      caretRef.current = drifted ? next.length : caret;
    } else {
      domValue.current = next;
    }
    onInput(next, caret);
  };

  // Bold is the ONE rich format the composer carries, and it carries it as the
  // `**markers**` the renderer reads — not the browser's own <b>, which readValue
  // (plain textContent) would silently drop on Post. So the native format input is
  // still swallowed below, and ⌘/Ctrl+B instead toggles the markers in the value
  // itself: the field rebuilds from the new value and draws the run heavy, and what
  // is stored is exactly what the feed will bold. The caret lands after the run.
  const applyBold = () => {
    const sel = readSelection();
    // ⌘B bolds a SELECTION. With nothing selected it does nothing — an earlier
    // version dropped an empty `****` pair to type between, but an empty pair is not
    // a bold run, so it showed as literal asterisks in the field (Vinnie, 23 Oct).
    // Select the text, then ⌘B — exactly what the composer help says it does.
    if (sel.start === sel.end) return;
    const edit = toggleBold(readValue(), sel.start, sel.end);
    // Keep the run selected after the toggle, so a second ⌘B turns it back off.
    selRef.current = { start: edit.start, end: edit.end };
    onInput(edit.value, edit.end);
  };
  const selectAll = () => {
    const el = elRef.current;
    if (!el) return;
    // A bare contenteditable does not reliably select its own contents on ⌘A, so do
    // it by hand — through the same offset map, from the start of the text to its
    // end — which also leaves the selection in the shape ⌘B then reads.
    placeSelection(0, readValue().length);
  };
  const onKeyDownLocal = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!disabled && (e.metaKey || e.ctrlKey) && !e.altKey && (e.key === 'b' || e.key === 'B')) {
      e.preventDefault();
      applyBold();
      return;
    }
    if (!disabled && (e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && (e.key === 'a' || e.key === 'A')) {
      e.preventDefault();
      selectAll();
      return;
    }
    onKeyDown(e);
  };

  // Keep it plain text otherwise: swallow the browser's own rich-formatting input
  // (so ⌘B never leaves an uncaptured <b> behind) and let the parent own Enter.
  // Paste is flattened to text, inserted at the caret.
  const onBeforeInput = (e: React.FormEvent) => {
    const it = (e.nativeEvent as InputEvent).inputType || '';
    if (it.startsWith('format')) e.preventDefault();
  };
  const onPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    if (!text) return;
    document.execCommand('insertText', false, text);
  };

  return (
    <>
      {/* The hidden ruler: same box as the field (it lives inside it and inherits
          the font and letter-spacing), used only to measure a marker's width. */}
      <span
        ref={rulerRef}
        aria-hidden="true"
        style={{ position: 'absolute', visibility: 'hidden', whiteSpace: 'pre', pointerEvents: 'none', left: -9999, top: 0 }}
      />
      <div
        ref={elRef}
        className={`${className} mention-field ce-field${disabled ? ' is-disabled' : ''}`}
        contentEditable={!disabled}
        suppressContentEditableWarning
        role="combobox"
        aria-label={ariaLabel}
        aria-multiline="true"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && matchesLen ? `${listId}-opt-${activeIdx}` : undefined}
        data-placeholder={placeholder}
        onInput={handleInput}
        onBeforeInput={onBeforeInput}
        onPaste={onPaste}
        onKeyDown={onKeyDownLocal}
        onKeyUp={() => onCaret(readValue(), readCaret())}
        onMouseUp={() => onCaret(readValue(), readCaret())}
      />
    </>
  );
}
